import { observer } from 'mobx-react-lite'
import { SwitchCheckbox } from '@/components/SwitchCheckbox'
import type { BoolQuestionAnswerEditorViewModel } from '@/features/jobs/questions/bool/BoolQuestionAnswerEditorViewModel'
import { AnswerEditorRendererContainer } from '@/features/jobs/questions/components/AnswerEditorRendererContainer'
import type { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import type { Question } from '@/features/jobs/questions/questions'
import type { SelectQuestionAnswerEditorViewModel } from '@/features/jobs/questions/select/SelectQuestionAnswerEditorViewModel'
import { AdvancedSelectContainer } from '@/features/jobs/questions/select/components/AdvancedSelect/AdvancedSelectContainer'
import type { PreviewInputGroupViewModel } from '@/features/legends/preview/PreviewInputGroupViewModel'
import { PreviewMutedText } from '@/features/legends/preview/components/PreviewOutput'
import {
  PreviewQuestionHeading,
  PreviewQuestionRow,
  PreviewQuestions,
} from '@/features/legends/preview/components/PreviewQuestions'

export interface PreviewQuestionsContainerProps {
  vm: PreviewInputGroupViewModel
  emptyText?: string
}

export const PreviewQuestionsContainer = observer(
  function PreviewQuestionsContainer({
    vm,
    emptyText,
  }: PreviewQuestionsContainerProps) {
    return (
      <PreviewQuestions isEmpty={vm.rows.length === 0} emptyText={emptyText}>
        {vm.rows.map((row) => {
          if (row.type === 'Documents') {
            const { question } = row
            return (
              <PreviewQuestionRow
                key={question.id}
                title={question.title}
                description={question.description}
              >
                <PreviewMutedText>
                  {question.options.map((option) => option.text).join(', ')}
                </PreviewMutedText>
              </PreviewQuestionRow>
            )
          }
          const { questionVm } = row
          if (questionVm.editor.type === 'Breaker') {
            return (
              <PreviewQuestionHeading
                key={questionVm.id}
                title={questionVm.title}
              />
            )
          }
          return (
            <PreviewQuestionRow
              key={questionVm.id}
              title={questionVm.title}
              description={questionVm.description}
              errors={questionVm.validationErrors}
              isCompactEditor={questionVm.editor.type === 'Bool'}
            >
              <PreviewAnswerEditorContainer
                questionId={questionVm.id}
                editorVm={questionVm.editor}
              />
            </PreviewQuestionRow>
          )
        })}
      </PreviewQuestions>
    )
  },
)

interface PreviewAnswerEditorContainerProps {
  questionId: string
  editorVm: QuestionAnswerEditorViewModelBase<Question>
}

/**
 * The job's answer editors, except that yes/no questions get a switch and
 * selects a dropdown, so that each question fits on one row.
 */
const PreviewAnswerEditorContainer = observer(
  function PreviewAnswerEditorContainer({
    questionId,
    editorVm,
  }: PreviewAnswerEditorContainerProps) {
    if (isBoolEditor(editorVm)) {
      return (
        <SwitchCheckbox
          trueLabel="Yes"
          falseLabel="No"
          value={!!editorVm.value}
          onChange={() => editorVm.respond(!editorVm.value)}
          testId={`bool-answer-${questionId}`}
        />
      )
    }
    if (isSelectEditor(editorVm)) {
      return (
        <AdvancedSelectContainer
          editorVm={editorVm}
          questionId={questionId}
          isDisabled={false}
        />
      )
    }
    return (
      <AnswerEditorRendererContainer
        questionId={questionId}
        editorVm={editorVm}
        isDisabled={false}
      />
    )
  },
)

function isBoolEditor(
  editorVm: QuestionAnswerEditorViewModelBase<Question>,
): editorVm is BoolQuestionAnswerEditorViewModel {
  return editorVm.type === 'Bool'
}

function isSelectEditor(
  editorVm: QuestionAnswerEditorViewModelBase<Question>,
): editorVm is SelectQuestionAnswerEditorViewModel {
  return editorVm.type === 'Select'
}
